import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Input, Separator } from "@lemonadetraining/web-ui";
import { IconCircleMinus } from "@tabler/icons-react";

/* Own Modules */
import { useEffectiveRegistry } from "../../hooks/useEffectiveRegistry";
import { useUserAccess } from "../../hooks/useUserAccess";
import type { MovementRegistryEntryWithMeta } from "../../api/movementRegistry";
import type { Movement } from "@lemonadetraining/contracts";

/**
 * Coach-facing movement-card row. Single-input combobox: a text field that
 * types-to-filter the effective MovementRegistry, with a results list that
 * opens beneath it. Picking an entry stamps `movementRegistryID` on the
 * Movement so the smart parser can personalize percentage-based prescriptions.
 *
 * The dropdown is portaled into the closest `[data-slot="dialog-content"]`
 * ancestor (or `document.body` outside a modal). Why not Radix Popover?
 * Inside a Radix Dialog, `react-remove-scroll` cancels every wheel event
 * whose target is not inside the Dialog's lockRef (DialogContent) or one
 * of its registered shards. A Popover portal renders into <body> — outside
 * the lockRef — so wheel scroll on its content is unconditionally cancelled.
 * Putting the dropdown inside DialogContent puts the wheel target inside
 * the lockRef, and overflow-y-auto can do its job.
 *
 * DialogContent uses `transform: translate(-50%, -50%)` to center; per
 * CSS, a transformed ancestor becomes the containing block for descendant
 * `position: fixed`. We use `position: absolute` and convert input rect to
 * DialogContent-relative coords so positioning stays predictable.
 */
interface MovementItemProps {
  id: number;
  movementData: Movement;
  onGetMovementData?: (data: Movement) => void;
  onRemove: (id: number) => void;
  editMode: boolean;
  /** Read at full contrast instead of as a disabled field. */
  readOnlyView?: boolean;
}

const parseRepsInput = (raw: string): number | string | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : trimmed;
};

const MovementItem: React.FC<MovementItemProps> = ({
  id,
  movementData,
  onGetMovementData,
  onRemove,
  editMode,
  readOnlyView = false,
}) => {
  const { boxID } = useUserAccess();
  const { status, movements: registry } = useEffectiveRegistry(boxID);

  const [registryID, setRegistryID] = useState<string | undefined>(movementData.movementRegistryID);
  const [percent, setPercent] = useState<number>(movementData.percent ?? 0);
  const [sets, setSets] = useState<number | undefined>(movementData.sets);
  const [reps, setReps] = useState<number | string | undefined>(movementData.reps);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState(0);

  const anchorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const blurTimeoutRef = useRef<number | null>(null);

  // Resolve the picked entry from the registry so the closed input shows the
  // canonical movement name.
  const pickedEntry = useMemo(
    () => (registryID ? (registry.find((m) => m.id === registryID) ?? null) : null),
    [registryID, registry]
  );

  const displayName = pickedEntry?.name ?? "";

  // Push field changes upstream when ours change. Excluding `onGetMovementData`
  // (recreated each parent render) prevents a parent ↔ child update loop.
  useEffect(() => {
    if (!onGetMovementData) return;
    onGetMovementData({
      id,
      movementRegistryID: registryID ?? "",
      sets,
      reps,
      percent,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryID, sets, reps, percent]);

  // When the popover closes (by any path), drop the typed query so reopening
  // starts fresh and the closed input shows the picked name rather than
  // stale search text.
  useEffect(() => {
    if (!popoverOpen) setSearchInput("");
  }, [popoverOpen]);

  // Cancel any pending blur-close timer on unmount so it can't fire after
  // the component is gone.
  useEffect(
    () => () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    },
    []
  );

  // Build BOTH a grouped view (for category-grouped rendering) and a flat
  // list (for keyboard navigation order). The flat list mirrors the visual
  // order so ArrowDown/Up step through items the way the user sees them.
  const { filteredGroups, flatItems } = useMemo(() => {
    const search = searchInput.trim().toLowerCase();
    const groups: Record<string, MovementRegistryEntryWithMeta[]> = {};
    for (const entry of registry) {
      if (search && !entry.name.toLowerCase().includes(search)) continue;
      const cat = entry.category ?? "other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(entry);
    }
    return { filteredGroups: groups, flatItems: Object.values(groups).flat() };
  }, [registry, searchInput]);

  const hasResults = flatItems.length > 0;

  // Reset highlight + scroll the list back to the top whenever the filtered
  // list changes — mirrors typical autocomplete UX.
  useEffect(() => {
    setActiveIndex(0);
    if (dropdownRef.current) dropdownRef.current.scrollTop = 0;
  }, [searchInput, registry]);

  // Portal target + positioning. Inside a Radix Dialog we portal into
  // DialogContent (so wheel events target inside the Dialog's lockRef and
  // scroll is allowed); outside any modal we portal to <body>. DialogContent
  // is transformed, so we use position:absolute relative to it; body has no
  // transform, so position:fixed with viewport coords works there.
  const [portalState, setPortalState] = useState<{
    target: HTMLElement;
    mode: "fixed" | "absolute";
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: "below" | "above";
  } | null>(null);

  useLayoutEffect(() => {
    if (!popoverOpen || !anchorRef.current) {
      setPortalState(null);
      return;
    }
    let dialogContent: HTMLElement | null = null;
    for (let el: HTMLElement | null = anchorRef.current; el; el = el.parentElement) {
      if (el.getAttribute?.("data-slot") === "dialog-content") {
        dialogContent = el;
        break;
      }
    }
    const target: HTMLElement = dialogContent ?? document.body;
    const mode: "fixed" | "absolute" = dialogContent ? "absolute" : "fixed";

    const updateCoords = (): void => {
      const inputRect = anchorRef.current?.getBoundingClientRect();
      if (!inputRect) return;

      // Available space inside the modal (or viewport when no modal).
      const containerRect = dialogContent
        ? dialogContent.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
      const margin = 8;
      const gap = 4;
      const desiredMaxH = 260;
      const minBelow = 160;
      const spaceBelow = containerRect.bottom - inputRect.bottom - margin - gap;
      const spaceAbove = inputRect.top - containerRect.top - margin - gap;

      // Default below; flip up when there's not enough space below within
      // the modal AND the input has more room above. Without this flip,
      // the dropdown gets clipped at the modal's bottom edge for inputs
      // sitting near the bottom of a short modal.
      const placement: "below" | "above" =
        spaceBelow < minBelow && spaceAbove > spaceBelow ? "above" : "below";
      const maxHeight = Math.max(
        80,
        Math.min(desiredMaxH, placement === "above" ? spaceAbove : spaceBelow)
      );

      const offsetTop = mode === "absolute" ? containerRect.top : 0;
      const offsetLeft = mode === "absolute" ? containerRect.left : 0;
      const top =
        placement === "above"
          ? inputRect.top - offsetTop - maxHeight - gap
          : inputRect.bottom - offsetTop + gap;
      const left = inputRect.left - offsetLeft;
      // Make the dropdown a bit wider than the input so long movement names
      // ("Burpee Box Jump Over") don't truncate.
      const width = Math.max(260, inputRect.width);

      setPortalState((prev) =>
        prev &&
        prev.target === target &&
        prev.mode === mode &&
        prev.top === top &&
        prev.left === left &&
        prev.width === width &&
        prev.maxHeight === maxHeight &&
        prev.placement === placement
          ? prev
          : { target, mode, top, left, width, maxHeight, placement }
      );
    };
    updateCoords();
    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);
    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [popoverOpen]);

  // Outside-click closes the dropdown. Listen on `click` (not `mousedown`)
  // so a scrollbar drag — which often targets <body> — doesn't trip the
  // close handler and prevent scrolling the results list.
  useEffect(() => {
    if (!popoverOpen) return;
    const onClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (!target) return;
      if (anchorRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setPopoverOpen(false);
    };
    document.addEventListener("click", onClickOutside);
    return () => document.removeEventListener("click", onClickOutside);
  }, [popoverOpen]);

  const onPickEntry = (entry: MovementRegistryEntryWithMeta): void => {
    setRegistryID(entry.id);
    setPopoverOpen(false);
  };

  // Move highlight via keyboard and scroll the active row into view. Hover
  // also updates activeIndex (so the highlight follows the cursor for
  // visual continuity) but must NOT scroll — that would fight a wheel
  // scroll, snapping the list back each tick.
  const moveActiveByKeyboard = (delta: number): void => {
    if (flatItems.length === 0) return;
    const newIdx = (activeIndex + delta + flatItems.length) % flatItems.length;
    setActiveIndex(newIdx);
    // `scrollIntoView` isn't implemented in jsdom; guard so unit tests don't crash.
    itemRefs.current[newIdx]?.scrollIntoView?.({ block: "nearest" });
  };

  return (
    <div className="flex flex-row items-end gap-1 mb-1">
      {editMode && (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          aria-label="Remove movement"
          onClick={() => onRemove(id)}
        >
          <IconCircleMinus className="size-4" />
        </Button>
      )}

      <div ref={anchorRef} className="w-[220px]">
        <Input
          className="w-full"
          // Closed: show the picked name. Open: show whatever the user is typing.
          value={popoverOpen ? searchInput : displayName}
          disabled={!editMode && !readOnlyView}
          readOnly={!editMode}
          placeholder="Search movement..."
          aria-label="Search movement"
          onChange={(e) => {
            setSearchInput(e.target.value);
            if (!popoverOpen) setPopoverOpen(true);
          }}
          onFocus={() => {
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
              blurTimeoutRef.current = null;
            }
            if (editMode) setPopoverOpen(true);
          }}
          onBlur={() => {
            // Tab out closes the dropdown. Delay so a click on a result
            // (which fires mousedown→onClick first) wins the race; if the
            // user re-focuses the input, the timeout is cancelled in
            // onFocus.
            blurTimeoutRef.current = window.setTimeout(() => {
              setPopoverOpen(false);
              blurTimeoutRef.current = null;
            }, 150);
          }}
          onKeyDown={(e) => {
            if (!editMode) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!popoverOpen) setPopoverOpen(true);
              else moveActiveByKeyboard(1);
            } else if (e.key === "ArrowUp") {
              if (popoverOpen) {
                e.preventDefault();
                moveActiveByKeyboard(-1);
              }
            } else if (e.key === "Enter") {
              if (popoverOpen && flatItems.length > 0) {
                e.preventDefault();
                onPickEntry(flatItems[activeIndex] ?? flatItems[0]);
              }
            } else if (e.key === "Escape") {
              if (popoverOpen) {
                e.preventDefault();
                setPopoverOpen(false);
              }
            }
          }}
        />
      </div>

      {popoverOpen &&
        portalState &&
        createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            aria-label="MovementRegistry results"
            style={{
              position: portalState.mode,
              top: portalState.top,
              left: portalState.left,
              width: portalState.width,
              maxHeight: portalState.maxHeight,
              zIndex: 10010,
            }}
            className="overflow-y-auto rounded-md border bg-popover shadow-md"
          >
            {status === "loading" && (
              <div className="p-2 text-sm text-muted-foreground">Loading movements…</div>
            )}
            {status === "error" && (
              <div className="p-2 text-sm text-destructive">
                Couldn’t load MovementRegistry. Try again.
              </div>
            )}
            {status === "ready" &&
              hasResults &&
              (() => {
                let flatIndex = -1;
                return Object.entries(filteredGroups).map(([group, items]) => (
                  <div key={group}>
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted">
                      {group}
                    </div>
                    {items.map((item) => {
                      flatIndex++;
                      const idx = flatIndex;
                      const isActive = idx === activeIndex;
                      return (
                        <button
                          key={item.id}
                          ref={(el) => {
                            itemRefs.current[idx] = el;
                          }}
                          type="button"
                          className={`w-full px-2 py-1.5 text-left text-sm cursor-pointer ${
                            isActive
                              ? "bg-accent text-accent-foreground"
                              : "hover:bg-accent hover:text-accent-foreground"
                          }`}
                          onMouseEnter={() => setActiveIndex(idx)}
                          // preventDefault on mousedown keeps focus on the
                          // input so the user can keep typing after picking
                          // (and stops onBlur from firing prematurely).
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onPickEntry(item)}
                        >
                          {item.name}
                        </button>
                      );
                    })}
                  </div>
                ));
              })()}
            {status === "ready" && !hasResults && (
              <div className="p-2 text-sm text-muted-foreground">No movements found.</div>
            )}
          </div>,
          portalState.target
        )}

      <Separator orientation="vertical" className="h-auto" />

      <div className="flex items-center gap-1">
        <Input
          type="number"
          aria-label="Sets"
          placeholder="Sets"
          className="w-14"
          min={0}
          value={sets ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            setSets(v === "" ? undefined : Number(v));
          }}
          readOnly={!editMode}
        />
        <span className="text-sm text-muted-foreground">×</span>
        <Input
          aria-label="Reps"
          placeholder="Reps"
          className="w-14"
          value={reps ?? ""}
          onChange={(e) => setReps(parseRepsInput(e.target.value))}
          readOnly={!editMode}
        />
        <span className="text-sm text-muted-foreground">@</span>
        <Input
          type="number"
          aria-label="Percent of 1RM"
          className="w-16"
          min={0}
          value={percent}
          onChange={(e) => setPercent(Number(e.target.value))}
          readOnly={!editMode}
        />
        <span className="text-sm text-muted-foreground">%</span>
      </div>
    </div>
  );
};

export default MovementItem;
